import { useState, useCallback, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { ChatHeader } from './chat-header';
import { ChatMessages } from './chat-messages';
import { ChatInput } from './chat-input';
import { ChatSuggestions } from './chat-suggestions';
import { ChatFooter } from './chat-footer';
import { ConfirmDialog } from './confirm-dialog';
import { resolveStrings, t } from './locales';
import { cn } from '../lib/utils';
import { useChanl } from './use-chanl-chat';
import { useVoice } from '@chanl/widget-sdk/react';
import type { VoiceMessage } from '@chanl/widget-sdk/react';
import type { ChanlUser, ClientContext } from '@chanl/widget-sdk';
import type { AgentConfig, ChatMessage, ChatTheme } from './types';
import type { ChatStrings } from './locales';

// Lazy-load the voice interface so framer-motion + VoiceClient only download
// on first mic click. Text-only users never pay the cost.
const VoiceInterface = lazy(() => import('./voice-interface'));

/**
 * Map a voice-transport transcript into a ChatMessage so it renders with
 * the same bubble styles as text chat. Voice user/agent become user/assistant.
 */
function voiceToChatMessage(v: VoiceMessage): ChatMessage {
  return {
    id: `voice-${v.id}`,
    role: v.sender === 'agent' ? 'assistant' : 'user',
    content: v.content,
    timestamp: v.timestamp instanceof Date ? v.timestamp.getTime() : Date.now(),
  };
}

// ── Props ──

interface ChatPageProps {
  agentId: string;
  theme: ChatTheme;
  branding: boolean;
  privacyUrl?: string;
  locale?: string;
  agentName?: string;
  agentSubtitle?: string;
  agentInitials?: string;
  agentAvatarUrl?: string;
  greeting?: string;
  suggestions?: string[];
  primaryColor?: string;
  placeholder?: string;
  darkBg?: string;
  strings?: Partial<ChatStrings>;
  // SDK mode — when both present, uses real API
  apiKey?: string;
  baseUrl?: string;
  /**
   * Open straight into a voice call instead of text chat (`?mode=voice`).
   *
   * For embedders whose entry point is a CALL affordance — e.g. the phone
   * button on chanl.ai's hero agent cards — so the button starts a call
   * rather than opening a chat the visitor then has to tap the mic in.
   * The browser still prompts for microphone permission as normal.
   */
  autoStartVoice?: boolean;
  // Verified end-user identity, decoded from the iframe URL params by the
  // chat route and threaded into the session so the agent knows the customer.
  user?: ChanlUser;
  userHash?: string;
  // Stable anonymous visitor id (from the `anonymousId` URL param / iframe
  // fallback). Threaded into BOTH chat and voice sessions so a cold visitor
  // dedupes to one Customer across channels (externalId = anon_<id>).
  anonymousId?: string;
  // HOST-page page-level context (pageUrl/referrer/utm) forwarded by the embed
  // loader as `ctx*` URL params and decoded by the chat route. Passed into
  // createSession's `input.clientContext` so these correct host-page values
  // override the iframe-local ones the chat-client collects.
  clientContext?: ClientContext;
}

// ── SDK Mode ──

function ChatPageSDK(props: ChatPageProps & { apiKey: string; baseUrl: string }) {
  const { agentId, apiKey, baseUrl, greeting, strings: stringOverrides } = props;

  // ── Live-updatable state (can be patched via postMessage 'chanl:update-config') ──
  // Initialized from props; the loader's ChanlChat.update() posts partial
  // updates here and these state values override what the render uses.
  const [theme, setTheme] = useState<ChatTheme>(props.theme);
  const [branding, setBranding] = useState<boolean>(props.branding);
  const [privacyUrl, setPrivacyUrl] = useState<string | undefined>(props.privacyUrl);
  const [locale, setLocale] = useState<string | undefined>(props.locale);
  const [placeholder, setPlaceholder] = useState<string | undefined>(props.placeholder);
  const [darkBg, setDarkBg] = useState<string | undefined>(props.darkBg);
  const [agentNameOverride, setAgentNameOverride] = useState<string | undefined>(props.agentName);
  const [agentSubtitleOverride, setAgentSubtitleOverride] = useState<string | undefined>(props.agentSubtitle);
  const [agentInitialsOverride, setAgentInitialsOverride] = useState<string | undefined>(props.agentInitials);
  const [agentAvatarUrlOverride, setAgentAvatarUrlOverride] = useState<string | undefined>(props.agentAvatarUrl);

  // Listen for live config updates from the loader. Missing / undefined keys
  // in the payload mean "don't touch". Empty strings mean "clear override".
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data || e.data.type !== 'chanl:update-config') return;
      const p = (e.data.payload ?? {}) as Record<string, unknown>;
      if ('theme' in p) setTheme((p.theme as ChatTheme) ?? 'dark');
      if ('branding' in p) setBranding(p.branding !== false);
      if ('privacyUrl' in p) setPrivacyUrl((p.privacyUrl as string) || undefined);
      if ('locale' in p) setLocale((p.locale as string) || undefined);
      if ('placeholder' in p) setPlaceholder((p.placeholder as string) || undefined);
      if ('darkBg' in p) setDarkBg((p.darkBg as string) || undefined);
      if ('agentName' in p) setAgentNameOverride((p.agentName as string) || undefined);
      if ('agentSubtitle' in p) setAgentSubtitleOverride((p.agentSubtitle as string) || undefined);
      if ('agentInitials' in p) setAgentInitialsOverride((p.agentInitials as string) || undefined);
      if ('agentAvatarUrl' in p) setAgentAvatarUrlOverride((p.agentAvatarUrl as string) || undefined);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const strings = resolveStrings(locale, stringOverrides);
  const isDark = theme === 'dark';

  const { messages, isTyping, error, send, agentConfig, resetSession, activeHandoff } = useChanl({ agentId, apiKey, baseUrl, greeting, user: props.user, userHash: props.userHash, anonymousId: props.anonymousId, clientContext: props.clientContext });
  // transport:'daily' — PCC-managed WebRTC via Daily. SmallWebRTC dispatch is
  // broken on PCC prod (accepts /start, never schedules a bot), so the embed
  // widget uses Daily for real-time voice.
  // user/userHash thread the identified visitor into the voice path — parity
  // with the chat path (useChanl above) so a logged-in user's voice call binds
  // their real Customer instead of an anon_<id> record (bug #46). Identity
  // threading is transport-agnostic; it applies if this ever falls back to WS.
  const voice = useVoice({ apiKey, baseUrl, anonymousId: props.anonymousId, user: props.user, userHash: props.userHash, transport: 'daily' });
  const [mode, setMode] = useState<'chat' | 'voice'>('chat');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [restartOpen, setRestartOpen] = useState(false);

  // Merge SDK-resolved agent config with form overrides. Overrides win so the
  // playground can test agent identity changes without touching the agent DB.
  const agent: AgentConfig = useMemo(() => {
    const base: AgentConfig = agentConfig ?? {
      id: agentId,
      name: agentNameOverride ?? 'AI Assistant',
      initials: agentInitialsOverride ?? 'AI',
      greeting: greeting ?? 'Hello!',
      suggestions: props.suggestions ?? [],
    };
    const merged = {
      ...base,
      name: agentNameOverride ?? base.name,
      subtitle: agentSubtitleOverride ?? base.subtitle,
      initials: agentInitialsOverride ?? base.initials,
      avatarUrl: agentAvatarUrlOverride ?? base.avatarUrl,
    };
    // A human took the thread over: the header must say WHO the visitor is
    // talking to now. The agent identity returns when the handoff clears
    // (new conversation) — see resetSession.
    if (activeHandoff?.origin === 'rep_takeover') {
      const repName = activeHandoff.repName ?? 'Support team member';
      return {
        ...merged,
        name: repName,
        subtitle: `with ${merged.name}`,
        initials: repName
          .split(/\s+/)
          .map((w) => w[0])
          .join('')
          .slice(0, 2)
          .toUpperCase(),
        avatarUrl: undefined,
      };
    }
    return merged;
  }, [
    agentConfig,
    agentId,
    agentNameOverride,
    agentSubtitleOverride,
    agentInitialsOverride,
    agentAvatarUrlOverride,
    greeting,
    props.suggestions,
    activeHandoff,
  ]);

  // Merge text chat messages with voice transcripts into a single timeline.
  // Voice transcripts render with the same bubble styles as text chat via
  // the `voiceToChatMessage` mapping. Sorted by timestamp so that if a
  // conversation interleaves text + voice (e.g., user types, then calls,
  // then types again) the order stays chronological.
  const mergedMessages: ChatMessage[] = useMemo(() => {
    if (voice.messages.length === 0) return messages;
    const voiceAsChat = voice.messages.map(voiceToChatMessage);
    return [...messages, ...voiceAsChat].sort((a, b) => a.timestamp - b.timestamp);
  }, [messages, voice.messages]);

  const handleSend = useCallback((text: string) => {
    setShowSuggestions(false);
    send(text);
  }, [send]);

  const handleStartVoice = useCallback(() => {
    setMode('voice');
    voice.start(agentId);
  }, [agentId, voice]);

  const handleEndVoice = useCallback(() => {
    voice.stop();
    setMode('chat');
  }, [voice]);

  // `?mode=voice` — start the call as soon as the widget is ready, once.
  // Guarded on status 'idle' so it cannot fight a call the user already
  // started, and on the ref so a re-render never dials twice.
  const autoVoiceFired = useRef(false);
  useEffect(() => {
    if (!props.autoStartVoice) return;
    if (autoVoiceFired.current) return;
    if (voice.status !== 'idle') return;
    autoVoiceFired.current = true;
    handleStartVoice();
  }, [props.autoStartVoice, voice.status, handleStartVoice]);

  // If the VoiceClient closes for any reason (remote hangup, network, error
  // after user already acknowledged), fall back to text mode automatically.
  useEffect(() => {
    if (mode === 'voice' && voice.status === 'closed') {
      setMode('chat');
    }
  }, [mode, voice.status]);

  return (
    <div
      className={cn('flex h-dvh flex-col bg-background', isDark && 'dark')}
      style={isDark ? { background: darkBg ?? 'linear-gradient(145deg, #1a2332, #243447)' } : undefined}
    >
      <ChatHeader
        agent={agent}
        onRestart={() => setRestartOpen(true)}
        restartLabel={strings.newChat}
        previousLabel={strings.previousConversations}
        emptyConversationsLabel={strings.noConversations}
      />

      {/* ── ChatMessages: ALWAYS visible, both modes ── */}
      <ChatMessages
        messages={mergedMessages}
        isTyping={isTyping}
        typingLabel={strings.typing}
        agentInitials={agent.initials}
        footer={
          showSuggestions && mode === 'chat' && agent.suggestions.length > 0 ? (
            <ChatSuggestions suggestions={agent.suggestions} onSelect={handleSend} />
          ) : undefined
        }
      />

      {error && mode === 'chat' && (
        <div className="shrink-0 px-6 py-2 text-center text-xs text-destructive">{error}</div>
      )}

      <ChatFooter branding={branding} privacyUrl={privacyUrl ?? 'https://chanl.ai/privacy'} strings={strings} />

      {/* ── Footer strip: swap ChatInput ↔ VoiceInterface based on mode ── */}
      {mode === 'chat' ? (
        <ChatInput
          onSend={handleSend}
          disabled={isTyping}
          placeholder={placeholder ?? t(strings.placeholder, agent.name)}
          onVoiceStart={handleStartVoice}
        />
      ) : (
        <Suspense
          fallback={
            <div className="shrink-0 px-6 pb-4 pt-2">
              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2 dark:border-white/[0.1] dark:bg-white/[0.05]">
                <span className="text-sm text-muted-foreground dark:text-white/60">Preparing voice call…</span>
              </div>
            </div>
          }
        >
          <VoiceInterface
            status={voice.status}
            duration={voice.duration}
            isMuted={voice.isMuted}
            error={voice.lastError}
            onToggleMute={voice.toggleMute}
            onEndCall={handleEndVoice}
          />
        </Suspense>
      )}

      <ConfirmDialog
        open={restartOpen}
        title={strings.restartTitle ?? 'Restart conversation?'}
        description={
          strings.restartDescription ??
          'This starts a fresh chat. You can still reopen this one from the menu.'
        }
        confirmLabel={strings.restartConfirm ?? 'Restart'}
        cancelLabel={strings.cancel ?? 'Cancel'}
        destructive
        onConfirm={() => {
          resetSession();
          setShowSuggestions(true);
          setRestartOpen(false);
        }}
        onCancel={() => setRestartOpen(false)}
      />
    </div>
  );
}

// ── Router ──

function ChatPageMissing({ theme, darkBg }: { theme: ChatTheme; darkBg?: string }) {
  const isDark = theme === 'dark';
  return (
    <div
      className={cn('flex h-dvh flex-col items-center justify-center bg-background', isDark && 'dark')}
      style={isDark ? { background: darkBg ?? 'linear-gradient(145deg, #1a2332, #243447)' } : undefined}
    >
      <div className="text-center px-6">
        <p className="text-lg font-semibold text-foreground dark:text-white">Chat not configured</p>
        <p className="mt-2 text-sm text-muted-foreground dark:text-white/50 max-w-sm">
          An <code className="font-mono text-xs bg-muted/50 dark:bg-white/10 px-1.5 py-0.5 rounded">apiKey</code> is required. Add it to the embed config.
        </p>
      </div>
    </div>
  );
}

export function ChatPage(props: ChatPageProps) {
  // baseUrl auto-resolves in the hook — only apiKey is mandatory
  if (props.apiKey) {
    return <ChatPageSDK {...props} apiKey={props.apiKey} baseUrl={props.baseUrl ?? ''} />;
  }
  return <ChatPageMissing theme={props.theme} darkBg={props.darkBg} />;
}
